import { useMemo } from 'react';
import { z, ZodSchema } from 'zod';

/**
 * Lightweight validation hook using Zod schemas
 *
 * Automatically validates data whenever it changes and returns validation state.
 *
 * @param schema - Zod schema to validate against
 * @param data - Data object to validate
 *
 * @returns Object with validation state: { isValid, errors, validatedData }
 *
 * @example
 * ```tsx
 * const schema = z.object({
 *   email: z.string().email(),
 *   age: z.number().min(18)
 * });
 *
 * const MyComponent = () => {
 *   const [email, setEmail] = useState('');
 *   const [age, setAge] = useState(0);
 *
 *   const { isValid, errors } = useZodValidation(schema, { email, age });
 *
 *   return (
 *     <>
 *       <Field type="text" value={email} onChange={(v) => setEmail(v)} invalid={!!errors.email} description={errors.mail} />
 *       <Field type="number" value={age} onChange={(v) => setAge(v)} invalid={!!errors.age} description={errors.age} />
 *     </>
 *   );
 * };
 * ```
 */
export const useZodValidation = <T extends Record<string, unknown>>(schema: ZodSchema<T>, data: T) => {
  /**
   * Validate data automatically when it changes
   */
  const validationResult = useMemo(() => {
    try {
      const validatedData = schema.parse(data);
      return {
        success: true,
        errors: {} as Partial<Record<keyof T, string>>,
        data: validatedData,
      };
    } catch (error) {
      if (error instanceof z.ZodError) {
        const errors: Partial<Record<keyof T, string>> = {};
        error.issues.forEach((err: z.ZodIssue) => {
          const fieldName = err.path[0] as keyof T;
          if (fieldName) {
            errors[fieldName] = err.message;
          }
        });
        return { success: false, errors, data: undefined };
      }
      return { success: false, errors: {} as Partial<Record<keyof T, string>>, data: undefined };
    }
  }, [schema, data]);

  return {
    /** Whether the data is valid */
    isValid: validationResult.success,
    /** Validation errors by field name */
    errors: validationResult.errors,
    /** Validated and typed data (only available if isValid is true) */
    validatedData: validationResult.data,
  };
};

/**
 * Type helper to infer the type from a Zod schema
 *
 * @example
 * ```tsx
 * const schema = z.object({ name: z.string() });
 * type FormData = InferSchema<typeof schema>; // { name: string }
 * ```
 */
export type InferSchema<T> = T extends ZodSchema<infer U> ? U : never;
